import { StateKey, TransferState } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

/**
 * Propaga el resultado de una request del render server al cliente vía
 * `TransferState`, evitando el re-fetch tras la hidratación (y con él, el
 * "flash" data → skeleton → data que se percibe al entrar directo por URL).
 *
 * Encapsula SÓLO el fragmento TransferState (leer+consumir en browser / guardar
 * en server). El `shareReplay` y cualquier cache en signal se componen por fuera,
 * en cada servicio, porque son responsabilidades específicas de cada uno.
 *
 * - En **browser**, si el server dejó datos bajo `key`: los lee, hace `remove`
 *   (para no retenerlos en memoria ni en el estado `ngh`, y para que una
 *   navegación posterior refetchee fresco) y los devuelve síncronos con `of`.
 *   Esa emisión síncrona es lo que impide que el estado `loading` inicial llegue
 *   a pintar el skeleton.
 * - En **server** (o browser sin key), se suscribe a `source$` y, sólo en server,
 *   serializa el valor emitido para que el cliente lo hidrate sin re-pedirlo.
 *
 * `isBrowser` se recibe como parámetro (no se inyecta `PLATFORM_ID`) para que el
 * util sea puro e importable desde cualquier bundle.
 */
export function withTransferState<T>(
  transferState: TransferState,
  isBrowser: boolean,
  key: StateKey<T>,
  source$: Observable<T>,
): Observable<T> {
  if (isBrowser && transferState.hasKey(key)) {
    const value = transferState.get<T>(key, null as unknown as T);
    transferState.remove(key);
    // Guardia defensiva: sólo servimos el snapshot si es un valor real. El
    // server hoy nunca serializa `null`/`undefined` (las fuentes emiten objeto
    // o `[]`), pero si un refactor futuro lo hiciera, caemos al fetch en vez de
    // propagar un valor nulo que algún consumidor desreferenciaría (p. ej. el
    // computed `renderItems` del home rompería con `for..of null`).
    if (value != null) {
      return of(value);
    }
  }

  return source$.pipe(
    tap((value) => {
      if (!isBrowser) {
        transferState.set(key, value);
      }
    }),
  );
}
